import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Printer, FileText, Maximize, Paperclip } from 'lucide-react';

interface OrderSummaryProps {
  paperType?: string;
  printer?: string;
  width?: number;
  height?: number;
  imageId?: string;
  originalWidthPx?: number;
  originalHeightPx?: number;
  paperRollWidthMm?: number;
}

const DetailItem = ({ icon: Icon, label, value }: { icon: React.ElementType, label: string, value: string | undefined | null }) => (
  value ? (
    <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center">
        <Icon className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
        <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-800 dark:text-white">{value}</span>
    </div>
  ) : null
);

const OrderSummary: React.FC<OrderSummaryProps> = ({
  paperType,
  printer,
  width,
  height,
  imageId,
  originalWidthPx,
  originalHeightPx,
  paperRollWidthMm,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 my-4 border border-gray-200 dark:border-gray-700">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('orderSummary.title')}</h3>

      {imageId ? (
        <div className="mb-4 rounded-lg overflow-hidden aspect-w-16 aspect-h-9 bg-gray-100 dark:bg-gray-700">
          <img
            src={`http://localhost:5000/uploads/${imageId}`}
            alt={t('orderSummary.imageAlt') || 'Uploaded image preview'}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="mb-4 rounded-lg flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 p-8 text-gray-500 dark:text-gray-400">
            <ImageIcon className="w-16 h-16 mb-2" />
            <p>{t('orderSummary.noImage', 'Image preview will appear here')}</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-lg text-gray-800 dark:text-gray-100 mb-2">{t('orderSummary.printDetails', 'Print Details')}</h4>
          <div className="space-y-1">
            <DetailItem icon={Paperclip} label={t('orderSummary.paperType', 'Paper')} value={paperType} />
            <DetailItem icon={Maximize} label={t('orderSummary.paperRollWidth', 'Paper Roll Width')} value={paperRollWidthMm ? `${paperRollWidthMm}mm` : null} />
            <DetailItem icon={Printer} label={t('orderSummary.printer', 'Printer')} value={printer} />
            <DetailItem icon={Maximize} label={t('orderSummary.printSize', 'Print Size')} value={width && height ? `${width}mm x ${height}mm` : null} />
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-lg text-gray-800 dark:text-gray-100 mb-2">{t('orderSummary.fileDetails', 'File Details')}</h4>
          <div className="space-y-1">
            <DetailItem icon={FileText} label={t('orderSummary.originalDimensions', 'Original Dimensions')} value={originalWidthPx && originalHeightPx ? `${originalWidthPx}px x ${originalHeightPx}px` : null} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
